import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton, 
  Tooltip
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  AccountTree,
  Category,
  ChevronLeft,
  ChevronRight,
  HelpOutline,
  Feedback,
  Checklist,
  VerifiedUser,
  Hub,
  MiscellaneousServices
} from "@mui/icons-material";
import { NetworkIcon } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useToast } from "../components/ToastContext";
import { useAuth } from "../services/authContext";
import PropTypes from 'prop-types';

// 1. Map the exact string names from the API 'icon' field to the React Components
const ICON_MAP = {
  Checklist: Checklist,
  HelpOutline: HelpOutline,
  Network: NetworkIcon,
  Category: Category,
  Feedback: Feedback,
  Hub: Hub,
  VerifiedUser: VerifiedUser,
  AccountTree: AccountTree,
  MiscellaneousServices: MiscellaneousServices
};

const Sidebar = ({ open, onToggle }) => {
  const theme = useTheme();
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useAuth();

  const [menuItems, setMenuItems] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true"; 
  });

  // 2. Load menus exactly as they come from the API and sort by menuOrder
  const loadDynamicMenus = () => {
    const storedMenus = sessionStorage.getItem('userMenus');
    if (storedMenus) {
      try {
        const apiMenus = JSON.parse(storedMenus);
        
        const sortedMenus = apiMenus.sort((a, b) => {
          const orderA = a.menuOrder !== undefined ? a.menuOrder : 99;
          const orderB = b.menuOrder !== undefined ? b.menuOrder : 99;
          return orderA - orderB;
        });

        setMenuItems(sortedMenus);
      } catch (e) {
        console.error("Error parsing dynamic menus", e);
      }
    } else {
      setMenuItems([]); 
    }
  };

  useEffect(() => {
    loadDynamicMenus();
    window.addEventListener('accountSwitched', loadDynamicMenus);
    return () => window.removeEventListener('accountSwitched', loadDynamicMenus);
  }, []);

  const handleNavigation = (path) => {
    if (path === "/logout") {
      logout();
      navigate("/login");
      toast.success("Logout successful");
    } else {
      navigate(path, { state: { reloadKey: Date.now() } });
    }
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsed", newState);
      return newState;
    });
  };

  return (
    <Box className="sidebar"
      sx={{
        width: !collapsed ? 60 : 200,
        bgcolor: theme.palette.background.sideBg,
        color: theme.palette.common.white,
        height: "100vh",
        position: "sticky", 
        marginTop: "-1px",
        transition: 'width 0.3s',
        zIndex: 11,
        overflowX: 'hidden',
      }}
    >
      <Box display="flex" justifyContent={!collapsed ? "center" : "flex-end"} p={1}>
        <IconButton onClick={toggleCollapse} sx={{ position: 'absolute', top: '0px' }}>
          {!collapsed ? <ChevronRight sx={{ color: theme.palette.common.white, width: '13px', height: '13px' }} /> : <ChevronLeft sx={{ color: theme.palette.common.white, width: '13px', height: '13px' }} />}
        </IconButton>
      </Box>
      <List sx={{ px: 1, py: 1 }}>
        {menuItems.map((item, index) => {
          const IconComponent = ICON_MAP[item.icon] || HelpOutline;
          
          const itemKey = item.menuCode || item.path || index;

          return (
            <Tooltip 
              key={itemKey} 
              title={item.label} 
              placement="right" 
              disableHoverListener={collapsed} 
              arrow 
              classes={{ tooltip: "sidebar-tooltip-bg", arrow: "sidebar-tooltip-arrow" }}
            >
              <ListItem
                component="button"
                onClick={() => handleNavigation(item.path)}
                sx={{
                  border: "none", cursor: "pointer", borderRadius: 1, py: 1.5, px: 1, justifyContent: "center",
                  bgcolor: location.pathname === item.path ? (theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.12)' : '#ffffffbd') : "transparent",
                  color: location.pathname === item.path ? (theme.palette.mode === 'dark' ? '#90caf9' : theme.palette.primary.main) : (theme.palette.mode === 'dark' ? 'text.secondary' : 'inherit'),
                  "&:hover": {
                    bgcolor: location.pathname === item.path ? (theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.16)' : '#ffffffbd') : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.08)'),
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    px: 1, 
                    color: location.pathname === item.path ? (theme.palette.mode === 'dark' ? '#90caf9' : theme.palette.primary.main) : (theme.palette.mode === 'dark' ? 'text.secondary' : theme.palette.common.white), 
                    minWidth: "auto", 
                    "& svg, & .MuiSvgIcon-root": { fontSize: "20px !important" }
                  }}
                >
                  <IconComponent size={18} />
                </ListItemIcon>
                {collapsed && (
                  <ListItemText 
                    primary={item.label} 
                    sx={{ m: 0, "& .MuiTypography-root": { fontSize: theme.typography.body2.fontSize, fontWeight: "bold" } }} 
                  />
                )}
              </ListItem>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,     
  onToggle: PropTypes.func.isRequired  
};

export default Sidebar;